import { Scene } from 'three';
import { USDZExporter } from 'three/examples/jsm/exporters/USDZExporter.js';

/**
 * ARHelper - Handles AR detection and iOS AR Quick Look support
 * Ported from Haxe ARHelper.hx
 */
export class ARHelper {
	public static readonly instance: ARHelper = new ARHelper();

	// Detect iOS devices (including iPad on iOS 13+ which reports as MacIntel).
	// The MacIntel + touch heuristic is guarded against Android user agents:
	// desktop device emulation (Chrome DevTools, the in-app browser) keeps the
	// real platform while faking touch points, which otherwise reads as an iPad.
	private readonly IS_IOS = (
		((/iPad|iPhone|iPod/giu).test(navigator.userAgent) && !(window as any).MSStream) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && !(/android/iu).test(navigator.userAgent)) ||
		((/iPad/giu).test(navigator.userAgent) && navigator.maxTouchPoints > 1)
	);

	// Detect Android devices. Checked AFTER iOS because a rare user agent can
	// mention both strings (e.g. desktop emulators); iOS takes priority since
	// iOS has its own native AR path (Quick Look).
	private readonly IS_ANDROID = !this.IS_IOS && (/android/iu).test(navigator.userAgent);

	// private constructor () {}

	/**
	 * Check if device supports iOS AR Quick Look
	 * Note: Chrome on iOS (CriOS) may not support relList.supports('ar') but can still use AR Quick Look
	 */
	public isArQuickLookCandidate (): boolean {
		if (this.IS_IOS) {
			// Check if <a rel="ar"> is supported (works in Safari)
			const tempAnchor = document.createElement('a');
			const supportsRelAR = Boolean(tempAnchor.relList &&
				tempAnchor.relList.supports &&
				tempAnchor.relList.supports('ar'));

			// If Safari supports it, return true
			if (supportsRelAR) {
				return true;
			}

			// Fallback: Check for Chrome on iOS (CriOS) or other iOS browsers that might support AR
			// Chrome on iOS uses WebKit and can sometimes trigger AR Quick Look even without relList support
			const { userAgent } = navigator;
			const isChromeIOS = (/CriOS/iu).test(userAgent);
			const isEdgeIOS = (/EdgiOS/iu).test(userAgent);
			const isFirefoxIOS = (/FxiOS/iu).test(userAgent);

			// Chrome/Edge/Firefox on iOS might support AR Quick Look
			if (isChromeIOS || isEdgeIOS || isFirefoxIOS) {
				// console.log('Detected iOS browser (Chrome/Edge/Firefox), attempting AR Quick Look support');
				return true;
			}

			return false;
		}
		return false;
	}

	/**
	 * Check if device supports WebXR AR
	 */
	public async supportsPresentation (): Promise<boolean> {
		try {
			if ('xr' in navigator) {
				const { xr } = navigator as any;
				if (xr && xr.isSessionSupported) {
					return await xr.isSessionSupported('immersive-ar');
				}
			}
			return false;
		} catch (error) {
			console.warn('Request to present in WebXR denied:', error);
			return false;
		}
	}

	/**
	 * Check if device is iOS (public method for external use)
	 */
	public isIOS (): boolean {
		return this.IS_IOS;
	}

	/**
	 * Check if device is Android (public method for external use).
	 * Android is routed to the `<model-viewer>` WebXR launch sheet; Google
	 * Scene Viewer is not used because it cannot read blob: URLs.
	 */
	public isAndroid (): boolean {
		return this.IS_ANDROID;
	}

	/**
	 * Open iOS AR Quick Look with USDZ model
	 */
	public async openIosArQuickLook (theScene: Scene): Promise<void> {
		try {
			// console.log('Preparing USDZ for iOS AR Quick Look...');
			// console.log('Scene info:', {
				// childrenCount: theScene.children.length,
				// hasIslandHolder: theScene.children.some((child) => child.type === 'Group')
			// });

			// Check if scene has content
			if (!theScene || theScene.children.length === 0) {
				throw new Error('Scene is empty. Cannot export to AR.');
			}

			// Generate USDZ from scene (this can take a while for complex models)
			// console.log('Starting USDZ export - this may take 30-90 seconds for complex models...');
			const exportStartTime = Date.now();
			const objectURL = await this.prepareUSDZ(theScene);
			const exportDuration = Math.round((Date.now() - exportStartTime) / 1000);
			// console.log(`USDZ prepared in ${exportDuration} seconds, objectURL:`, objectURL);

			if (!objectURL) {
				throw new Error('Failed to generate USDZ file.');
			}

			// Create model URL with hash parameters
			const modelUrl = new URL(objectURL, window.location.href);

			// Disable content scaling (fixed size)
			if (!modelUrl.hash) {
				modelUrl.hash = '#';
			} else if (!modelUrl.hash.includes('allowsContentScaling')) {
				modelUrl.hash += '&';
			}
			modelUrl.hash += 'allowsContentScaling=0';

			// Create anchor element for AR Quick Look
			// iOS AR Quick Look requires: rel="ar", href pointing to USDZ, and an img child
			const anchor = document.createElement('a');
			anchor.setAttribute('rel', 'ar');
			anchor.setAttribute('href', modelUrl.href);
			anchor.setAttribute('download', 'model.usdz');
			anchor.style.display = 'none';

			// Add placeholder image (required by AR Quick Look)
			const img = document.createElement('img');
			anchor.appendChild(img);

			// Validate anchor setup before clicking
			if (!anchor.href || !anchor.href.startsWith('blob:')) {
				throw new Error(`Invalid USDZ URL: ${anchor.href}. The USDZ file may not have been created correctly.`);
			}

			if (!anchor.rel || anchor.rel !== 'ar') {
				throw new Error('AR anchor rel attribute is missing or incorrect.');
			}

			if (!anchor.querySelector('img')) {
				throw new Error('AR anchor is missing required image element.');
			}

			// Append to body before clicking
			document.body.appendChild(anchor);

			if (!anchor.isConnected) {
				throw new Error('AR anchor failed to attach to DOM. Cannot trigger AR Quick Look.');
			}

			// console.log('Attempting to present in AR with Quick Look...');
			// console.log('Anchor element:', {
				// href: anchor.href,
				// rel: anchor.rel,
				// hasImg: anchor.querySelector('img') !== null,
				// isConnected: anchor.isConnected,
				// protocol: window.location.protocol,
				// isSecureContext: window.isSecureContext
			// });

			// iOS requires the click to happen synchronously within the user gesture
			// Since USDZ export is async, we need to click immediately after it completes
			// Click must happen synchronously - no requestAnimationFrame or other async delays
			// Trigger click immediately after USDZ is ready
			// Note: iOS may reject AR Quick Look if the click happens outside the user gesture context
			// This can happen if the USDZ export takes too long (>1-2 seconds)
			try {
				// Check if we're still in a secure context
				if (!window.isSecureContext) {
					throw new Error('Page is no longer in a secure context. AR Quick Look requires HTTPS.');
				}

				// Verify blob URL is accessible
				if (!objectURL || !objectURL.startsWith('blob:')) {
					throw new Error('USDZ blob URL is invalid or not accessible.');
				}

				anchor.click();
				// console.log('AR Quick Look anchor clicked');

				// Note: We can't reliably detect if AR Quick Look actually opened
				// iOS may show "can't start AR immediately" if:
				// 1. The user gesture context was lost (export took too long)
				// 2. The USDZ file is invalid or corrupted
				// 3. iOS AR Quick Look is disabled or unavailable
				// If AR doesn't open, the user will see iOS's error message
			} catch (clickError: any) {
				// Cleanup on click error
				try {
					if (anchor.parentNode) {
						if (img.parentNode === anchor) {
							anchor.removeChild(img);
						}
						document.body.removeChild(anchor);
					}
					URL.revokeObjectURL(objectURL);
				} catch (cleanupError) {
					console.warn('Error during AR cleanup after click error:', cleanupError);
				}
				throw new Error(`Failed to trigger AR Quick Look: ${clickError?.message || 'Anchor click failed'}. If you see "can't start AR immediately", the USDZ export may have taken too long and the user gesture context was lost. Please try clicking the AR button again immediately.`);
			}

			// Cleanup after a short delay (async cleanup is fine)
			setTimeout(() => {
				try {
					if (anchor.parentNode) {
						if (img.parentNode === anchor) {
							anchor.removeChild(img);
						}
						document.body.removeChild(anchor);
					}
					URL.revokeObjectURL(objectURL);
					// console.log('AR Quick Look cleanup complete');
				} catch (cleanupError) {
					console.warn('Error during AR cleanup:', cleanupError);
				}
			}, 500);
		} catch (error: any) {
			console.error('Error opening iOS AR Quick Look:', error);
			// Enhance error message with context for user visibility
			const errorMessage = error?.message || 'Unknown error occurred';

			// Provide more specific error messages based on what failed
			let userFriendlyMessage = errorMessage;

			if (errorMessage.includes('USDZ export')) {
				userFriendlyMessage = `USDZ export failed: ${errorMessage}. The kitchen model may be too complex or there may be an issue with the 3D data.`;
			} else if (errorMessage.includes('gesture context')) {
				userFriendlyMessage = `AR activation failed: ${errorMessage}. Please try clicking the AR button again immediately.`;
			} else if (errorMessage.includes('Invalid USDZ URL')) {
				userFriendlyMessage = `USDZ file creation failed: ${errorMessage}. Please try again.`;
			} else if (errorMessage.includes('anchor')) {
				userFriendlyMessage = `AR setup failed: ${errorMessage}. Please ensure you're using Safari on iOS.`;
			}

			const enhancedError = new Error(userFriendlyMessage);
			(enhancedError as any).originalError = error;
			throw enhancedError;
		}
	}

	/**
	 * Prepare USDZ file from scene
	 */
	private async prepareUSDZ (scene: Scene): Promise<string> {
		// console.log('Preparing USDZ...');

		// Count scene complexity before optimization
		let preOptMeshCount = 0;
		let preOptObjectCount = 0;
		scene.traverse(() => {
			preOptObjectCount++;
		});
		scene.traverse((obj: any) => {
			if (obj.type === 'Mesh') {
				preOptMeshCount++;
			}
		});

		// console.log('Scene for USDZ export (before optimization):', {
		// 	childrenCount: scene.children.length,
		// 	totalObjects: preOptObjectCount,
		// 	meshCount: preOptMeshCount,
		// 	children: scene.children.map((child) => ({
		// 		type: child.type,
		// 		name: child.name,
		// 		childrenCount: child.children?.length || 0
		// 	}))
		// });

		// Work on a deep clone so the live scene's lights, materials, and geometries are not mutated.
		const exportScene = this.cloneSceneForExport(scene);

		// Optimize clone for export - disable shadows and simplify materials
		this.optimizeSceneForExport(exportScene);

		// Warn if scene is still very complex
		if (preOptMeshCount > 500) {
			console.warn(`Scene has ${preOptMeshCount} meshes. USDZ export may take a long time or timeout.`);
		}

		// For simple scenes, use a shorter timeout (30 seconds)
		// For complex scenes, use longer timeout (90 seconds)
		const exportTimeout = preOptMeshCount > 100 ? 90000 : 30000;

		// Validate scene before export
		if (!scene || !scene.children || scene.children.length === 0) {
			throw new Error('Scene is empty or invalid. Cannot export to USDZ.');
		}

		// Additional validation: check if scene has any meshes
		let hasMeshes = false;
		scene.traverse((obj: any) => {
			if (obj.type === 'Mesh' && obj.geometry && obj.geometry.attributes && obj.geometry.attributes.position) {
				hasMeshes = true;
			}
		});

		if (!hasMeshes) {
			throw new Error('Scene has no exportable meshes. Cannot export to USDZ.');
		}

		const exporter = new USDZExporter();

		// USDZExporter.parse() returns a Promise (not callbacks)
		// Wrap with timeout and progress tracking
		const startTime = Date.now();
		let progressInterval: NodeJS.Timeout | null = null;

		// Show progress every 5 seconds for long exports
		progressInterval = setInterval(() => {
			const elapsed = Math.round((Date.now() - startTime) / 1000);
			// console.log(`USDZ export in progress... ${elapsed} seconds elapsed`);
		}, 5000);

		// console.log('Calling USDZExporter.parse()...', {
			// sceneChildren: scene.children.length,
			// meshCount: preOptMeshCount,
			// timeout: `${exportTimeout / 1000} seconds`
		// });

		try {
			// In three 0.163, parse() is `async parse(scene, options)` returning a Promise<Uint8Array>;
			// the installed @types/three declares the newer callback-based signature, so the method is
			// cast to the actual runtime shape. parseAsync() does not exist in three 0.163.0.
			// 4096 is the practical ceiling for iOS AR Quick Look across all
			// currently-supported iPhones/iPads. 8192 is unreliable on older
			// devices (textures silently fail to render). The user already sees
			// ArLoadingBanner while we wait, so the extra export time is OK.
			const parseUsdz = exporter.parse as unknown as (scene: Scene, options?: { maxTextureSize?: number }) => Promise<Uint8Array<ArrayBuffer>>;
			const parsePromise = parseUsdz(exportScene, { maxTextureSize: 4096 });

			// Add timeout wrapper
			const timeoutPromise = new Promise<Uint8Array<ArrayBuffer>>((_, reject) => {
				setTimeout(() => {
					const elapsed = Math.round((Date.now() - startTime) / 1000);
					reject(new Error(`USDZ export timed out after ${elapsed} seconds. ${preOptMeshCount > 100 ? 'The kitchen model may be too complex. Try simplifying the design or reducing the number of components.' : 'The export appears to be stuck. Please try again or contact support if the issue persists.'}`));
				}, exportTimeout);
			});

			// Race between parse and timeout
			const arrayBuffer = await Promise.race([parsePromise, timeoutPromise]);

			// Clear progress interval
			if (progressInterval) clearInterval(progressInterval);

			const elapsed = Math.round((Date.now() - startTime) / 1000);

			if (!arrayBuffer || arrayBuffer.length === 0) {
				throw new Error('USDZ export returned empty result. The scene may not have any exportable geometry.');
			}

			// console.log(`USDZ export completed in ${elapsed} seconds, size:`, arrayBuffer.length, 'bytes');

			// Create blob and object URL
			const blob = new Blob([arrayBuffer], {
				type: 'model/vnd.usdz+zip'
			});

			const url = URL.createObjectURL(blob);
			// console.log('USDZ prepared successfully, blob URL:', url, 'size:', blob.size, 'bytes');

			return url;
		} catch (error: any) {
			// Clear progress interval on error
			if (progressInterval) clearInterval(progressInterval);

			const elapsed = Math.round((Date.now() - startTime) / 1000);
			console.error(`USDZ export error after ${elapsed} seconds:`, error);

			const errorMessage = error?.message || String(error) || 'Unknown error';
			throw new Error(`USDZ export failed: ${errorMessage}. Please try again or contact support if the issue persists.`);
		}
	}

	/**
	 * Optimize scene for USDZ export by disabling expensive features and simplifying geometry
	 */
	/**
	 * Deep-clone a scene so export-time mutation (material map removal, geometry attribute deletion,
	 * light removal) does not affect the live scene. Object3D.clone shares material and geometry
	 * references by default, so we clone those explicitly.
	 */
	private cloneSceneForExport (scene: Scene): Scene {
		const cloned = scene.clone(true) as Scene;
		cloned.traverse((object: any) => {
			if (object.geometry && typeof object.geometry.clone === 'function') {
				object.geometry = object.geometry.clone();
			}
			if (object.material) {
				if (Array.isArray(object.material)) {
					object.material = object.material.map((mat: any) => (mat && typeof mat.clone === 'function' ? mat.clone() : mat));
				} else if (typeof object.material.clone === 'function') {
					object.material = object.material.clone();
				}
			}
		});
		return cloned;
	}

	private optimizeSceneForExport (scene: Scene): void {
		// console.log('Optimizing scene for USDZ export...');
		let meshCount = 0;
		let materialCount = 0;
		let simplifiedMeshes = 0;
		const objectsToRemove: any[] = [];

		scene.traverse((object: any) => {
			// Disable shadows for all objects (reduces export complexity)
			if (object.castShadow !== undefined) {
				object.castShadow = false;
			}
			if (object.receiveShadow !== undefined) {
				object.receiveShadow = false;
			}

			// Simplify materials - remove expensive texture maps
			if (object.material) {
				materialCount++;
				if (Array.isArray(object.material)) {
					object.material.forEach((mat: any) => {
						this.simplifyMaterial(mat);
					});
				} else {
					this.simplifyMaterial(object.material);
				}
			}

			// Simplify geometry for meshes. Keep normals — USDZ/AR Quick Look
			// relies on per-vertex normals for PBR lighting; deleting them
			// leaves everything flat-shaded.
			if (object.type === 'Mesh' && object.geometry) {
				meshCount++;
				const { geometry } = object;

				if (geometry.attributes.tangent) {
					geometry.deleteAttribute('tangent');
					simplifiedMeshes++;
				}
				if (geometry.attributes.color) {
					geometry.deleteAttribute('color');
					simplifiedMeshes++;
				}

				if (geometry.attributes.position) {
					const { position } = geometry.attributes;
					if (position.count < 3) {
						objectsToRemove.push(object);
					}
				}
			}

			// Remove helper objects, lights, cameras, etc.
			if (object.type === 'Helper' ||
				object.type === 'Light' ||
				object.type === 'Camera' ||
				object.type === 'Line' ||
				object.type === 'Points') {
				objectsToRemove.push(object);
			}
		});

		// Remove identified objects
		objectsToRemove.forEach((obj) => {
			if (obj.parent) {
				obj.parent.remove(obj);
			}
		});

		// Update world matrices after optimization
		scene.updateMatrixWorld(true);

		// console.log('Scene optimization complete:', {
			// meshCount,
			// materialCount,
			// simplifiedMeshes,
			// removedObjects: objectsToRemove.length,
			// totalObjects: scene.children.length
		// });
	}

	/**
	 * Simplify material by removing expensive texture maps, but keep base textures
	 */
	private simplifyMaterial (mat: any): void {
		if (!mat) return;

		// Keep the PBR maps iOS AR Quick Look actually uses: map (albedo),
		// normalMap, roughnessMap, metalnessMap, aoMap, emissiveMap. Stripping
		// them leaves materials looking flat and plasticky in AR. Only drop
		// displacementMap (not supported by USDZExporter) and bumpMap (we use
		// normalMap instead).
		mat.displacementMap = null;
		mat.bumpMap = null;

		if (mat.emissive && mat.emissiveIntensity && mat.emissiveIntensity > 1) {
			mat.emissiveIntensity = 1;
		}

		if (mat.flatShading !== undefined) {
			mat.flatShading = false;
		}

		mat.needsUpdate = true;
	}
}

